import {
  Body,
  Button,
  Card,
  Cell,
  ColumnHeaderCell,
  Header,
  Row,
  Table,
  Tag,
  Typography,
} from "@hellocarbo/lasagne";
import { FileSpreadsheetIcon, Import } from "lucide-react";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionFunctionArgs,
  data,
  LoaderFunctionArgs,
  useLoaderData,
  useParams,
} from "react-router";
import invariant from "tiny-invariant";

import EditActions from "@/components/ImportDeposit/EditActions";
import ImportDepositEmptyView from "@/components/ImportDeposit/EmptyView";
import PageWrapper from "@/components/ui/PageWrapper";
import { FeaturesContext } from "@/contexts";
import { FeatureFlags, TImportDeposit, TScoreInfo } from "@/types";
import { fromImportStatusToIntent } from "@/utils/importDeposits";

export async function loader({ params }: LoaderFunctionArgs) {
  const { scoreId } = params;
  invariant(scoreId, "No scoreId found!");

  // Loaders cannot access context data or other route data, so we need to do this request again here.
  // To be removed when import_deposit_view_react FF is removed
  const featureFlagsResponse = await fetch("/api/client/features/");

  if (featureFlagsResponse.status !== 200) {
    throw data(
      "Failed to load the user feature flags in ImportDeposit loader",
      {
        status: featureFlagsResponse.status,
      }
    );
  }

  const { data: featureFlags } = (await featureFlagsResponse.json()) as {
    data: FeatureFlags;
  };

  if (!featureFlags || !featureFlags["import_deposit_view_react"]) {
    return { importDeposits: [], scoreInfo: null };
  }

  const importDepositsResponse = await fetch(
    `/api/client/scores/${scoreId}/import_deposits`
  );
  if (importDepositsResponse.status !== 200) {
    throw data("Failed to load the Import Deposits from this score", {
      status: importDepositsResponse.status,
    });
  }

  const { data: importDeposits } = (await importDepositsResponse.json()) as {
    data: Array<TImportDeposit>;
  };

  const scoreInfoResponse = await fetch(`/api/client/scores/${scoreId}`);
  if (scoreInfoResponse.status !== 200) {
    throw data("Failed to load the Score info", {
      status: scoreInfoResponse.status,
    });
  }

  const { data: scoreInfo } = (await scoreInfoResponse.json()) as {
    data: TScoreInfo;
  };

  return { importDeposits, scoreInfo };
}

export async function action({ request }: ActionFunctionArgs) {
  const data = await request.formData();
  const scoreId = data.get("scoreId") as string;
  const importId = data.get("importId") as string;
  const actionType = data.get("actionType") as string;

  let cancelImportResponse;
  if (actionType === "cancel") {
    cancelImportResponse = await fetch(
      `/api/client/scores/${scoreId}/import_deposits/${importId}/cancel`,
      {
        method: request.method,
      }
    );
  } else if (actionType === "delete") {
    cancelImportResponse = await fetch(
      `/api/client/scores/${scoreId}/import_deposits/${importId}/delete_related_surveys`,
      {
        method: request.method,
      }
    );
  }

  if (cancelImportResponse?.status !== 200) {
    return { ok: false };
  }

  return { ok: true };
}

function ImportDeposit() {
  const { t } = useTranslation("importDeposit");
  const { scoreId } = useParams();
  const { importDeposits, scoreInfo } = useLoaderData<typeof loader>();

  const { featureFlags } = useContext(FeaturesContext);
  if (!featureFlags || !featureFlags["import_deposit_view_react"]) {
    return null;
  }

  invariant(scoreInfo, "No score information found!");

  const baseUrl = `/scores/${scoreId}/import_deposits`;
  const shouldDisplayEmptyView = !importDeposits.length;

  return (
    <PageWrapper>
      <div className="my-lg mx-xl rounded-lg">
        {shouldDisplayEmptyView ? (
          <ImportDepositEmptyView
            urlNew={`${baseUrl}/new`}
            isScoreReadOnly={Boolean(scoreInfo.is_readonly)}
          />
        ) : (
          <Card isFlat>
            <Typography step="1" as="h1" weight="bold" className="mb-md">
              {t("importDeposit:title")}
            </Typography>
            {!scoreInfo.is_readonly && (
              <Button
                as="a"
                href={`${baseUrl}/new`}
                intent="positive"
                shape="soft"
                size="small"
                className="mb-md text-center"
              >
                <Import className="size-sm" />
                {t("importDeposit:newImport.importFile")}
              </Button>
            )}
            <Table>
              <Header>
                <Row>
                  {[
                    "fileName",
                    "dataType",
                    "importDate",
                    "numberLine",
                    "status",
                    "actions",
                  ].map((header) => (
                    <ColumnHeaderCell>
                      <Typography
                        key={header}
                        step="-1"
                        className="py-3xs text-grayscale text-left"
                      >
                        {t(`importDeposit:importTable.${header}`)}
                      </Typography>
                    </ColumnHeaderCell>
                  ))}
                </Row>
              </Header>
              <Body>
                {importDeposits.map((importDeposit) => (
                  <Row key={importDeposit.id}>
                    <Cell className="py-sm flex items-center">
                      <FileSpreadsheetIcon className="mr-xs text-primary-light" />
                      <Typography step="-1" weight="semibold" className="py-sm">
                        {importDeposit.input_file}
                      </Typography>
                    </Cell>
                    <Typography
                      as="td"
                      step="-1"
                      weight="semibold"
                      className="py-sm"
                    >
                      {t(
                        `importDeposit:importTypeList.${importDeposit.import_type}`
                      )}
                    </Typography>
                    <Typography
                      as="td"
                      step="-1"
                      weight="semibold"
                      className="py-sm"
                    >
                      {t("formatter:date", {
                        value: new Date(importDeposit.created_at),
                      })}
                    </Typography>
                    <Typography
                      as="td"
                      step="-1"
                      weight="semibold"
                      className="py-sm"
                    >
                      {t("formatter:number", {
                        value: importDeposit.total_rows,
                      })}
                    </Typography>

                    <Cell>
                      <Tag
                        text={t(
                          `importDeposit:importDepositStatus.${importDeposit.status}`
                        )}
                        intent={fromImportStatusToIntent(importDeposit.status)}
                      />
                    </Cell>
                    <EditActions
                      importDepositId={importDeposit.id}
                      importDepositStatus={importDeposit.status}
                      isScoreReadOnly={Boolean(scoreInfo.is_readonly)}
                      detailUrl={`${baseUrl}/${importDeposit.id}`}
                    />
                  </Row>
                ))}
              </Body>
            </Table>
          </Card>
        )}
      </div>
    </PageWrapper>
  );
}

export default ImportDeposit;
